// Columnas apiladas por mes (HU-34b, `ui/MG-ILE-1092.md`, «Gráficos: forma elegida»). Presentación
// pura: recibe las cifras ya contadas y las dibuja, sin lógica de negocio.
//
// SIN LIBRERÍA DE GRÁFICOS NI ESTILOS EN LÍNEA (`estandares_frontend.md` §6): cada columna es un
// `svg` de alto fijo con `rect` cuyas alturas van como ATRIBUTOS SVG en porcentaje del mayor total
// de la serie, y el color en clases de token (`fill-marca` el total, `fill-rose-600` la parte). La
// cifra exacta va escrita encima como texto («etiqueta directa» del lineamiento): la proporción es
// solo dibujo. Con toda la serie en 0 no se divide: queda la línea base y la cifra 0.
//
// El dibujo es `aria-hidden`: la alternativa textual es la cifra visible y la tabla de datos que la
// pantalla pone junto al gráfico (HU-34b-C15). Sin animación de entrada (lineamiento).

import type { ReactNode } from 'react';

/** Un mes de la serie: su clave (`aaaa-mm`), la etiqueta corta («nov»), el total y la parte. */
export type ColumnaDelMes = { clave: string; etiqueta: string; total: number; parte: number };

/** El alto, en %, de `valor` respecto del mayor de la serie. Sin mayor (la serie entera en 0), nada. */
const proporcion = (valor: number, mayor: number): number => (mayor > 0 ? (Math.max(valor, 0) / mayor) * 100 : 0);

/** Separación entre la parte y el resto de la columna (2 px en blanco). */
const SEPARACION = 2;

function DibujoDeLaColumna({ total, parte, mayor }: Readonly<{ total: number; parte: number; mayor: number }>) {
  const alto = proporcion(total, mayor);
  const altoDeLaParte = proporcion(Math.min(parte, total), mayor);
  return (
    <svg aria-hidden="true" focusable="false" className="h-40 w-full overflow-visible">
      {alto > 0 && <rect className="fill-marca" x="15%" width="70%" y={`${100 - alto}%`} height={`${alto}%`} rx="2" />}
      {altoDeLaParte > 0 && (
        <rect className="fill-rose-600" x="15%" width="70%" y={`${100 - altoDeLaParte}%`} height={`${altoDeLaParte}%`} rx="2" />
      )}
      {altoDeLaParte > 0 && altoDeLaParte < alto && (
        <line className="stroke-white" x1="15%" x2="85%" y1={`${100 - altoDeLaParte}%`} y2={`${100 - altoDeLaParte}%`}
          strokeWidth={SEPARACION} />
      )}
      <line className="stroke-hairline" x1="0" x2="100%" y1="100%" y2="100%" strokeWidth="2" />
    </svg>
  );
}

/**
 * Doce celdas (o las que traiga la serie) en una rejilla; en cada una, la cifra del total, la
 * columna y la etiqueta del mes. `leyenda` va bajo el gráfico, en texto.
 */
export default function ColumnasPorMes({ columnas, leyenda, testidDeColumna, 'data-testid': testid, 'aria-labelledby': etiquetadoPor }: Readonly<{
  columnas: ColumnaDelMes[];
  leyenda?: ReactNode;
  /** El testid de cada columna; cada una lleva además `data-mes` con su clave. */
  testidDeColumna?: string;
  'data-testid'?: string;
  /** El id del título que nombra el gráfico (el de su tarjeta). */
  'aria-labelledby'?: string;
}>) {
  const mayor = Math.max(0, ...columnas.map((c) => c.total));
  return (
    <figure data-testid={testid} aria-labelledby={etiquetadoPor} className="m-0">
      <ol className="grid grid-cols-12 gap-0.5 sm:gap-1.5 list-none m-0 p-0">
        {columnas.map((c, i) => (
          <li key={c.clave} data-testid={testidDeColumna} data-mes={c.clave} className="flex min-w-0 flex-col items-center gap-1">
            <span className="text-micro tabular-nums slashed-zero text-ink">{c.total}</span>
            <DibujoDeLaColumna total={c.total} parte={c.parte} mayor={mayor} />
            {/* A 390 px cada columna mide unos 20 px y «nov» a 12 px casi lo mismo: las etiquetas
                se tocaban («novdicene…»). Por debajo de `sm` se escalonan en dos filas. */}
            <span className={'text-micro text-ink-soft whitespace-nowrap' + (i % 2 === 1 ? ' max-sm:pt-4' : '')}>{c.etiqueta}</span>
          </li>
        ))}
      </ol>
      {leyenda && <figcaption className="mt-3">{leyenda}</figcaption>}
    </figure>
  );
}

/** Una marca de color para la leyenda, con su texto: el color nunca va solo (1.4.1). */
export function MarcaDeLaLeyenda({ clase, children }: Readonly<{ clase: string; children: ReactNode }>) {
  return (
    <span className="inline-flex items-center gap-1.5 text-meta text-ink-soft">
      <svg aria-hidden="true" focusable="false" className="h-2.5 w-2.5 shrink-0">
        <rect className={clase} width="100%" height="100%" rx="2" />
      </svg>
      {children}
    </span>
  );
}
